import { useEffect, useState } from "react";
import ModalComponent from "../../../Components/ModalComponent";
import { Button, Col, Form, Input, Radio, Row, Select, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "../../../apis/store";
import { useDispatch, useSelector } from "react-redux";
import {
  ToEditCar,
  toGetMyVehicle,
} from "../../../apis/actions/vehicle.actions";
import { showNotitfication } from "../../../utils/Utils";
import { LuLayoutTemplate } from "react-icons/lu";

interface MyCarsEditProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  selectedData: any;
}

const MyCarsEdit = ({
  showModal,
  setShowModal,
  selectedData,
}: MyCarsEditProps) => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const [showInput, setShowInput] = useState(true);
  const [number, setNumber] = useState("");
  const [letter1, setLetter1] = useState("");
  const [letter2, setLetter2] = useState("");
  const [letter3, setLetter3] = useState("");
  const [initialFormValues, setInitialFormValues] = useState<any>({});
  const [hasChanged, setHasChanged] = useState(false);

  const { loading } = useSelector((state: RootState) => state.vehicle.EditCar);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (showModal && selectedData) {
      const cleanedLetters = (selectedData?.plate_letters || "").replace(
        /\s+/g,
        ""
      );
      const letterArray = cleanedLetters.split("");

      const initialValues = {
        model_id: selectedData?.model_id,
        vin_sn: selectedData?.vin_sn,
        plate_numbers: selectedData?.plate_numbers,
        title: selectedData?.title || "",
        plate_letters: {
          letter1: letterArray[0] || "",
          letter2: letterArray[1] || "",
          letter3: letterArray[2] || "",
        },
      };

      setInitialFormValues(initialValues);
      setLetter1(initialValues.plate_letters.letter1);
      setLetter2(initialValues.plate_letters.letter2);
      setLetter3(initialValues.plate_letters.letter3);
      setNumber(initialValues.plate_numbers || "");
      form.setFieldsValue({
        model_id: initialValues.model_id,
        vin_sn: initialValues.vin_sn,
        plate_numbers: initialValues.plate_numbers,
        title: initialValues.title,
      });
    }
  }, [showModal, selectedData, form]);
  useEffect(() => {
    const currentValues = form.getFieldsValue();

    const isFormChanged =
      currentValues.title !== initialFormValues.title ||
      currentValues.vin_sn !== initialFormValues.vin_sn ||
      currentValues.plate_numbers !== initialFormValues.plate_numbers ||
      letter1 !== initialFormValues.plate_letters?.letter1 ||
      letter2 !== initialFormValues.plate_letters?.letter2 ||
      letter3 !== initialFormValues.plate_letters?.letter3;

    setHasChanged(isFormChanged);
  }, [form, number, letter1, letter2, letter3]);
  const handleRadioChange = (e: any) => {
    setShowInput(e.target.value === "saudia");
  };
  const arabicOptions = [
    { value: "أ", label: "أ" },
    { value: "ب", label: "ب" },
    { value: "ح", label: "ح" },
    { value: "د", label: "د" },
    { value: "ر", label: "ر" },
    { value: "س", label: "س" },
    { value: "ص", label: "ص" },
    { value: "ط", label: "ط" },
    { value: "ع", label: "ع" },
    { value: "ق", label: "ق" },
    { value: "ك", label: "ك" },
    { value: "ل", label: "ل" },
    { value: "م", label: "م" },
    { value: "ن", label: "ن" },
    { value: "هـ", label: "هـ" },
    { value: "و", label: "و" },
    { value: "ى", label: "ى" },
  ];

  const englishOptions = [
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "J", label: "J" },
    { value: "D", label: "D" },
    { value: "R", label: "R" },
    { value: "S", label: "S" },
    { value: "X", label: "X" },
    { value: "T", label: "T" },
    { value: "E", label: "E" },
    { value: "G", label: "G" },
    { value: "K", label: "K" },
    { value: "L", label: "L" },
    { value: "Z", label: "Z" },
    { value: "N", label: "N" },
    { value: "H", label: "H" },
    { value: "U", label: "U" },
    { value: "V", label: "V" },
  ];
  const handleApply = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      delete values.model_id;

      const plate_letters = showInput
        ? `${convertArabicToEnglishletters(letter1) || ""}${
            convertArabicToEnglishletters(letter2) || ""
          }${convertArabicToEnglishletters(letter3) || ""}`
        : "";
      const plate_letters_ar = showInput
        ? [letter1, letter2, letter3]
            .map(convertEnglishToArabicLetters)
            .filter(Boolean)
            .join(" ")
        : "";
      const plate_numbers = convertArabicToEnglish(
        form.getFieldValue("plate_numbers")
      );
      const plate_numbers_ar = showInput
        ? convertEnglishToArabic(form.getFieldValue("plate_numbers"))
        : "";

      const applyData = {
        ...values,
        plate_letters,
        plate_letters_ar,
        plate_numbers,
        plate_numbers_ar,
        vehicle_id: selectedData?.id,
      };
      const success = (await dispatch(ToEditCar(applyData))) as any;
      const edited = success?.payload?.data?.result;

      if (edited?.code === 200) {
        showNotitfication(edited?.message, "success");
        dispatch(toGetMyVehicle(i18n.language));
        setShowModal(false);
      } else {
        showNotitfication(edited?.message, "error");
      }
    } catch (error) {}
  };
  const convertArabicToEnglish = (value: string = "") => {
    if (typeof value !== "string") return "";

    const arabicToEnglishMap: { [key: string]: string } = {
      "٠": "0",
      "١": "1",
      "٢": "2",
      "٣": "3",
      "٤": "4",
      "٥": "5",
      "٦": "6",
      "٧": "7",
      "٨": "8",
      "٩": "9",
    };

    return value.replace(/[٠-٩]/g, (d) => arabicToEnglishMap[d] || d);
  };
  const convertEnglishToArabic = (value: string = "") => {
    if (typeof value !== "string") return "";

    const englishToArabicMap: { [key: string]: string } = {
      "0": "٠",
      "1": "١",
      "2": "٢",
      "3": "٣",
      "4": "٤",
      "5": "٥",
      "6": "٦",
      "7": "٧",
      "8": "٨",
      "9": "٩",
    };

    return value.replace(/[0-9]/g, (d) => englishToArabicMap[d] || d);
  };

  const arabicToEnglishMap = {
    أ: "A",
    ب: "B",
    ح: "J",
    د: "D",
    ر: "R",
    س: "S",
    ص: "X",
    ط: "T",
    ق: "G",
    ك: "K",
    ل: "L",
    م: "Z",
    ن: "N",
    ه: "H",
    و: "U",
    ى: "V",
    ع: "E",
  };

  const convertArabicToEnglishletters = (arabicText: string) => {
    return arabicText
      .split("")
      .map(
        (char) =>
          arabicToEnglishMap[char as keyof typeof arabicToEnglishMap] || char
      )
      .join("");
  };
  const englishToArabicMap = Object.entries(arabicToEnglishMap).reduce(
    (acc, [arabic, english]) => {
      acc[english] = arabic;
      return acc;
    },
    {} as Record<string, string>
  );

  const convertEnglishToArabicLetters = (englishText: string) => {
    return englishText
      .split("")
      .map((char) => englishToArabicMap[char] || char)
      .join("");
  };
  return (
    <ModalComponent
      title={
        <div className="ac--order-details-header">
          <span className="text-md-bold ac--color-text-profile">
            {t("profile.car.editCar")}
          </span>
        </div>
      }
      open={showModal}
      onClose={() => setShowModal(false)}
      closeOnOutsideClick={true}
      width={500}
      modalStyle={{ top: 150 }}
      showCloseIcon={false}
    >
      <Form
        form={form}
        layout="vertical"
        className="mt-20"
        onValuesChange={() => {
          const currentValues = form.getFieldsValue();
          const isFormChanged =
            currentValues.title !== initialFormValues.title ||
            currentValues.vin_sn !== initialFormValues.vin_sn ||
            currentValues.plate_numbers !== initialFormValues.plate_numbers ||
            letter1 !== initialFormValues.plate_letters?.letter1 ||
            letter2 !== initialFormValues.plate_letters?.letter2 ||
            letter3 !== initialFormValues.plate_letters?.letter3;

          setHasChanged(isFormChanged);
        }}
      >
        {" "}
        <div className="ac--cars-edit-container">
          <img src={`${selectedData?.image_128}`} alt="cars" />
        </div>
        <Row gutter={[16, 16]} className="mt-20">
          <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              label={t("profile.car.title")}
              name="title"
              labelCol={{ className: "text-md-bold" }}
            >
              <Input type="text" className="ac--input-login" size="large" />
            </Form.Item>
          </Col>
          <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              label={t("profile.car.model")}
              name="model_id"
              labelCol={{ className: "text-md-bold" }}
            >
              <Input
                type="text"
                className="ac--input-login"
                size="large"
                readOnly
              />
            </Form.Item>
          </Col>
          <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              label={t("profile.car.vin")}
              name="vin_sn"
              labelCol={{ className: "text-md-bold" }}
            >
              <Input
                className="ac--input-login"
                size="large"
                type="text"
                maxLength={17}
                value={form.getFieldValue("vin_sn")}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, "");
                  form.setFieldsValue({ vin_sn: onlyNumbers });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16} className="mt-20 mb-20">
          <Col span={24}>
            <Radio.Group
              onChange={handleRadioChange}
              defaultValue="saudia"
              className="ac--radio-check"
            >
              <Radio
                value="saudia"
                className={
                  i18n.language === "en" ? "ac--font-jfbold" : "ac--font-expo"
                }
              >
                {t("profile.car.saudi")}
              </Radio>
              <Radio
                value="others"
                className={
                  i18n.language === "en" ? "ac--font-jfbold" : "ac--font-expo"
                }
              >
                {t("profile.car.others")}
              </Radio>
            </Radio.Group>
          </Col>
        </Row>
        {showInput ? (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label=""
                  name="plate_numbers"
                  rules={[
                    {
                      required: true,
                      message: t("profile.messages.6"),
                    },
                  ]}
                >
                  <div className="form-group">
                    <Input
                      className="ac--input-login"
                      size="large"
                      type="text"
                      placeholder={t("profile.car.plate")}
                      maxLength={4}
                      value={number}
                      prefix={<LuLayoutTemplate size={17} />}
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/\D/g, "");
                        setNumber(onlyNumbers);
                        form.setFieldsValue({ plate_numbers: onlyNumbers });
                      }}
                    />
                  </div>
                </Form.Item>
              </Col>

              <Col span={4}>
                <Select
                  className="ac--select-plate"
                  onChange={(value) => setLetter1(value)}
                  style={{ width: 60 }}
                  value={letter1}
                  options={
                    i18n.language == "ar" ? arabicOptions : englishOptions
                  }
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Col>
              <Col span={4}>
                <Select
                  className="ac--select-plate"
                  onChange={(value) => setLetter2(value)}
                  style={{ width: 60 }}
                  value={letter2}
                  options={
                    i18n.language == "ar" ? arabicOptions : englishOptions
                  }
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Col>
              <Col span={4}>
                <Select
                  className="ac--select-plate"
                  onChange={(value) => setLetter3(value)}
                  style={{ width: 60 }}
                  value={letter3}
                  options={
                    i18n.language == "ar" ? arabicOptions : englishOptions
                  }
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label=""
                  name="plate_numbers"
                  rules={[
                    {
                      required: true,
                      message: t("profile.messages.6"),
                    },
                  ]}
                >
                  <div className="form-group">
                    <Input
                      className="ac--input-login"
                      size="large"
                      type="text"
                      placeholder={t("profile.car.plate")}
                      prefix={<LuLayoutTemplate size={17} />}
                      value={number}
                      onChange={(value) => setNumber(value.target.value)}
                    />
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-20" justify="end">
          <Button
            className="text-md ac--btn-edit-save"
            onClick={handleApply}
            disabled={loading === true || !hasChanged}
          >
            {loading === true ? t("btn.loading") : t("btn.edit")}
          </Button>
        </Row>
      </Form>
    </ModalComponent>
  );
};

export default MyCarsEdit;
